(() => {
  'use strict';

  const ROOT_SELECTOR = '[data-afc-cart]';
  const timers = new Map();

  const formatMoney = (cents, currency) => {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency: currency || 'USD'
      }).format(Number(cents || 0) / 100);
    } catch (_) {
      return `$${(Number(cents || 0) / 100).toFixed(2)}`;
    }
  };

  const updateBadges = (count) => {
    document.querySelectorAll('[data-cart-count], .af-header__bag span, .cart-count-bubble span:first-child').forEach((node) => {
      node.textContent = String(count);
      node.hidden = false;
    });
  };

  const replaceAuxiliarySection = (html, selector) => {
    if (!html) return;
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const next = doc.querySelector(selector);
    const current = document.querySelector(selector);
    if (next && current) current.replaceWith(next);
  };

  const initCart = (root) => {
    if (!root || root.dataset.afcReady === 'true') return;
    root.dataset.afcReady = 'true';

    const sectionId = root.dataset.sectionId;
    const currency = root.dataset.currency || 'USD';
    const status = root.querySelector('[data-afc-status]');

    const setStatus = (message, error = false) => {
      if (!status) return;
      status.textContent = message || '';
      status.style.color = error ? '#ff7772' : '';
    };

    const renderCurrentSection = (html, focusKey, focusAction) => {
      if (!html) return false;
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const nextRoot = doc.querySelector(ROOT_SELECTOR);
      if (!nextRoot) return false;
      root.replaceWith(nextRoot);
      initCart(nextRoot);
      window.Shopify?.PaymentButton?.init?.();
      if (focusKey) {
        const line = [...nextRoot.querySelectorAll('[data-afc-line]')].find((item) => item.dataset.key === focusKey);
        line?.querySelector(`[data-afc-action="${focusAction}"]`)?.focus({ preventScroll: true });
      }
      return true;
    };

    const changeLine = async (line, quantity, focusAction = 'plus') => {
      if (!line || !sectionId) return;
      const key = line.dataset.key;
      if (!key) return;
      line.classList.add('is-loading');
      setStatus('Updating your bag…');

      try {
        const sections = [sectionId, 'cart-drawer', 'cart-icon-bubble'];
        const response = await fetch(window.routes?.cart_change_url || '/cart/change.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({
            id: key,
            quantity,
            sections,
            sections_url: window.location.pathname
          })
        });
        const payload = await response.json().catch(() => ({}));
        if (!response.ok || payload.errors || payload.status) {
          throw new Error(payload.description || payload.message || payload.errors || 'The bag could not be updated.');
        }

        updateBadges(payload.item_count || 0);
        replaceAuxiliarySection(payload.sections?.['cart-drawer'], 'cart-drawer');
        replaceAuxiliarySection(payload.sections?.['cart-icon-bubble'], '#shopify-section-cart-icon-bubble');

        const replaced = renderCurrentSection(payload.sections?.[sectionId], quantity > 0 ? key : null, focusAction);
        if (!replaced) window.location.reload();
      } catch (error) {
        console.error(error);
        line.classList.remove('is-loading', 'is-removing');
        setStatus(error.message || 'The bag could not be updated.', true);
      }
    };

    const previewLine = (line, quantity) => {
      const total = line.querySelector('[data-afc-line-total]');
      const unitPrice = Number(line.dataset.unitPrice || 0);
      if (total) total.textContent = formatMoney(unitPrice * quantity, currency);
    };

    const scheduleChange = (line, quantity, focusAction) => {
      const key = line.dataset.key;
      window.clearTimeout(timers.get(key));
      timers.set(key, window.setTimeout(() => {
        timers.delete(key);
        changeLine(line, quantity, focusAction);
      }, 240));
    };

    root.addEventListener('click', (event) => {
      const button = event.target.closest('[data-afc-action]');
      if (!button || !root.contains(button)) return;
      const line = button.closest('[data-afc-line]');
      const input = line?.querySelector('[data-afc-quantity-input]');
      if (!line || !input) return;

      const action = button.dataset.afcAction;
      const step = Math.max(1, Number(input.step || 1));
      const current = Math.max(0, Number(input.value || 0));

      if (action === 'remove') {
        line.classList.add('is-removing');
        changeLine(line, 0, 'remove');
        return;
      }

      const next = action === 'plus' ? current + step : Math.max(0, current - step);
      input.value = String(next);
      previewLine(line, next);
      if (next === 0) line.classList.add('is-removing');
      scheduleChange(line, next, action);
    });

    root.addEventListener('change', (event) => {
      const input = event.target.closest('[data-afc-quantity-input]');
      if (!input) return;
      const line = input.closest('[data-afc-line]');
      const quantity = Math.max(0, Number(input.value || 0));
      input.value = String(quantity);
      previewLine(line, quantity);
      scheduleChange(line, quantity, 'plus');
    });

    root.querySelector('[data-afc-note]')?.addEventListener('change', async (event) => {
      try {
        await fetch(window.routes?.cart_update_url || '/cart/update.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: {'Content-Type': 'application/json', Accept: 'application/json'},
          body: JSON.stringify({ note: event.target.value })
        });
        setStatus('Order note saved.');
      } catch (_) {
        setStatus('The order note could not be saved.', true);
      }
    });

    root.querySelector('[data-afc-discount-apply]')?.addEventListener('click', () => {
      const input = root.querySelector('[data-afc-discount-input]');
      const code = input?.value?.trim();
      if (!code) {
        setStatus('Enter a discount code first.', true);
        input?.focus();
        return;
      }
      const rootUrl = (window.Shopify?.routes?.root || '/').replace(/\/?$/, '/');
      window.location.assign(`${rootUrl}discount/${encodeURIComponent(code)}?redirect=${encodeURIComponent(window.location.pathname)}`);
    });
  };

  const initialise = (scope = document) => {
    if (scope.matches?.(ROOT_SELECTOR)) initCart(scope);
    scope.querySelectorAll?.(ROOT_SELECTOR).forEach(initCart);
  };

  document.addEventListener('DOMContentLoaded', () => initialise());
  document.addEventListener('shopify:section:load', (event) => initialise(event.target));
})();
